import { createBrowserRouter } from "react-router";
import * as Sentry from '@sentry/react';
// import { QueryClient } from "@tanstack/react-query";
import { RootLayout, AuthLayout, AccountLayout } from "@/components/Layout";
import { GeneralError, MaintenanceError, NotFoundError, ErrorBoundary } from "@/pages/Errors";
import { Loader } from "@/components/Loader";
// import { queryClient } from "@/utils/query-client";
// import { fetchAuthSession } from "@aws-amplify/auth";

// const projectsQuery = () => ({
//   queryKey: ['projects'],
//   queryFn: async () => {
//     const { accessToken } = (await fetchAuthSession()).tokens ?? {};
//     const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/projects`, {
//       headers: {
//         Authorization: `Bearer ${accessToken}`,
//       },
//     });
//     const data = await response.json();
//     return data;
//   },
// })

// const projectsLoader = (queryClient: QueryClient) =>
//   async () => {
//     const query = projectsQuery()
//     return (
//       queryClient.getQueryData(query.queryKey) ??
//       (await queryClient.fetchQuery(query))
//     );
//   };

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    hydrateFallbackElement: <Loader />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: "/",
            // loader: projectsLoader(queryClient),
            children: [
              {
                element: <AccountLayout />,
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (await import("@/pages")).Home,
                    }),
                  },
                  {
                    path: "profile",
                    lazy: async () => ({
                      Component: (await import("@/pages")).Profile,
                    }),
                  },
                  {
                    path: "billing",
                    lazy: async () => ({
                      Component: (await import("@/pages")).Billing,
                    }),
                  },
                  {
                    path: "team",
                    lazy: async () => ({
                      Component: (await import("@/pages")).Team,
                    }),
                  },
                  {
                    path: "account-tokens",
                    lazy: async () => ({
                      Component: (await import("@/pages")).AccountTokens,
                    }),
                  },
                  {
                    path: "onboarding",
                    lazy: async () => ({
                      Component: (await import("@/pages")).Onboarding,
                    }),
                  },
                  {
                    path: "projects",
                    lazy: async () => ({
                      Component: (await import("@/pages")).Home,
                    }),
                  },
                ],
              },
              {
                path: "projects/:projectSlug",
                lazy: async () => ({
                  Component: (await import("@/components/Project")).Layout,
                }),
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (await import("@/pages/Projects")).Project,
                    }),
                  },
                  {
                    path: "get-started",
                    lazy: async () => ({
                      Component: (await import("@/pages/Projects")).GetStarted,
                    }),
                  },
                  {
                    path: "settings",
                    lazy: async () => ({
                      Component: (await import("@/components/Project/Settings")).Layout,
                    }),
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import("@/pages/Projects/Settings")).General,
                        }),
                      },
                      {
                        path: "api-keys",
                        lazy: async () => ({
                          Component: (await import("@/pages/Projects/Settings")).ApiKeys,
                        }),
                      },
                      {
                        path: "danger-zone",
                        lazy: async () => ({
                          Component: (await import("@/pages/Projects/Settings")).DangerZone,
                        }),
                      },
                    ],
                  },
                  {
                    path: "users",
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import("@/pages/Projects")).Users,
                        }),
                      },
                      {
                        path: ":userId",
                        lazy: async () => ({
                          Component: (await import("@/components/Project/Users/User")).Layout,
                        }),
                        children: [
                          {
                            index: true,
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/User")).Profile,
                            }),
                          },
                          {
                            path: "notifications",
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/User")).Notifications,
                            }),
                          },
                          {
                            path: "subscriptions",
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/User")).Subscriptions,
                            }),
                          },
                          {
                            path: "preferences",
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/User")).Preferences,
                            }),
                          },
                          {
                            path: "channels",
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/User")).ChannelSettings,
                            }),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "notiflows",
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import("@/pages/Projects")).Notiflows,
                        }),
                      },
                      {
                        path: ":notiflowHandle",
                        lazy: async () => ({
                          Component: (await import("@/components/Project/Notiflows/Notiflow")).Layout,
                        }),
                        children: [
                          {
                            index: true,
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/Notiflow")).Overview,
                            }),
                          },
                          {
                            path: "steps/:stepId?",
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/Notiflow")).Steps,
                            }),
                          },
                          {
                            path: "changes",
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/Notiflow/Changes")).Changes,
                            }),
                          },
                          {
                            path: "runs",
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/Notiflow/Runs")).Runs,
                            }),
                          },
                        ],
                      },
                      {
                        path: ":notiflowHandle",
                        children: [
                          {
                            path: "runs/:runId",
                            lazy: async () => ({
                              Component: (await import("@/components/Project/Notiflows/Notiflow/Runs/Run/Layout")).Layout,
                            }),
                            children: [
                              {
                                index: true,
                                lazy: async () => ({
                                  Component: (await import("@/pages/Projects/Notiflow/Run")).Overview,
                                }),
                              },
                              {
                                path: "notifications",
                                lazy: async () => ({
                                  Component: (await import("@/pages/Projects/Notiflow/Run")).Notifications,
                                }),
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "notifications",
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import("@/pages/Projects/Notifications")).Notifications,
                        }),
                      },
                      {
                        path: ":notificationId",
                        children: [
                          {
                            index: true,
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/Notification")).Notification,
                            }),
                          },
                          {
                            path: "deliveries/:deliveryId",
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/Notification")).Delivery,
                            }),
                            children: [
                              {
                                index: true,
                                lazy: async () => ({
                                  Component: (await import("@/pages/Projects/Notification")).DeliveryOverview,
                                }),
                              },
                              {
                                path: "logs/:logId?",
                                lazy: async () => ({
                                  Component: (await import("@/pages/Projects/Notification")).DeliveryLogs,
                                }),
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "channels",
                    children: [
                      {
                        index: true,
                        path: "new?",
                        lazy: async () => ({
                          Component: (await import("@/pages/Projects")).Channels,
                        }),
                      },
                      {
                        path: ":channelId",
                        lazy: async () => ({
                          Component: (await import("@/pages/Projects")).Channel,
                        }),
                      }
                    ],
                  },
                  {
                    path: "topics",
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import("@/pages/Projects")).Topics,
                        }),
                      },
                      {
                        path: ":topicName",
                        lazy: async () => ({
                          Component: (await import("@/components/Project/Topics/Topic")).Layout,
                        }),
                        children: [
                          {
                            index: true,
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/Topic")).Overview,
                            }),
                          },
                          {
                            path: "subscriptions",
                            lazy: async () => ({
                              Component: (await import("@/pages/Projects/Topic")).Subscriptions,
                            }),
                          },
                        ],
                      },
                      {
                        path: ":topicName/subscriptions/:subscriptionId",
                        lazy: async () => ({
                          Component: (await import("@/pages/Projects/Topic")).Subscription,
                        }),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            children: [
              {
                path: "/projects/:projectSlug/notiflows/:notiflowHandle/steps/:stepId/template",
                lazy: async () => ({
                  Component: (await import("@/pages/Projects/Notiflow/Steps")).Template,
                }),
              },
            ],
          },
        ],
      },
      {
        lazy: async () => ({
          Component: (await import("@/components/Auth")).Layout,
        }),
        children: [
          {
            path: "login",
            lazy: async () => ({
              Component: (await import("@/pages")).Login,
            }),
          },
          {
            path: "signup",
            lazy: async () => ({
              Component: (await import("@/pages")).Login,
            }),
          },
        ],
      },
    ],
  },
  { path: '500', Component: GeneralError },
  { path: '404', Component: NotFoundError },
  { path: '503', Component: MaintenanceError },
  { path: '*', Component: NotFoundError },
]);

export default router;
